import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Save } from "lucide-react"
import { useAuth } from "../contexts/AuthContext"
import { profileAPI } from '../lib/api'
import LocationPicker from "./LocationPicker"
import PageMeta from "./PageMeta"

function EditProfilePage() {
  const navigate = useNavigate()
  const { user, profile: currentProfile, loading: authLoading, updateProfile } = useAuth()

  // Auth guard — redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true })
    }
  }, [user, authLoading, navigate])

  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    display_name: "",
    username: "",
    bio: "",
    avatar_url: "",
  })
  const [locationData, setLocationData] = useState({})
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (currentProfile) {
      setFormData({
        display_name: currentProfile.display_name || "",
        username: currentProfile.username || "",
        bio: currentProfile.bio || "",
        avatar_url: currentProfile.avatar_url || "",
      })

      // Initialize location data from current profile
      setLocationData({
        country_code: currentProfile.country_code || "",
        country_name: currentProfile.country_name || "",
        region_code: currentProfile.region_code || "",
        region_name: currentProfile.region_name || "",
        city_locality: currentProfile.city_locality || currentProfile.city || "",
        city_sublocality: currentProfile.city_sublocality || "",
        postal_code: currentProfile.postal_code || "",
        latitude: currentProfile.latitude || null,
        longitude: currentProfile.longitude || null,
        city_place_id: currentProfile.city_place_id || "",
        location_source: currentProfile.location_source || "",
        location_confidence: currentProfile.location_confidence || 0,
      })
    }
  }, [currentProfile])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    // Clear errors when typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }))
    }
  }

  const handleLocationChange = (location) => {
    setLocationData(location)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.display_name.trim()) {
      newErrors.display_name = "Display name is required"
    }

    if (!formData.username.trim()) {
      newErrors.username = "Username is required"
    } else if (!/^[a-zA-Z0-9_]{3,20}$/.test(formData.username)) {
      newErrors.username = "Username must be 3-20 characters (letters, numbers, underscore only)"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Check username availability before saving
  const checkUsernameAvailable = async (username) => {
    if (username === currentProfile?.username) return true
    const { data } = await profileAPI.getProfile(username)
    return !data || data.id === user?.id
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    try {
      // Check username availability
      const usernameOk = await checkUsernameAvailable(formData.username)
      if (!usernameOk) {
        setErrors({ username: 'Username is already taken' })
        setSaving(false)
        return
      }

      const updateData = {
        ...formData,
        // Include location data with proper field mapping
        country: locationData.country_name || formData.country,
        city: locationData.city_locality || formData.city,
        country_code: locationData.country_code,
        country_name: locationData.country_name,
        region_code: locationData.region_code,
        region_name: locationData.region_name,
        city_locality: locationData.city_locality,
        city_sublocality: locationData.city_sublocality,
        postal_code: locationData.postal_code,
        latitude: locationData.latitude,
        longitude: locationData.longitude,
        city_place_id: locationData.city_place_id,
        location_source: locationData.location_source,
        location_confidence: locationData.location_confidence,
        email: currentProfile.email,
      }

      // Single DB write + context update via AuthContext
      const { data, error } = await updateProfile(updateData)

      if (error?.status === 409) {
        setErrors({ username: 'Username is already taken' })
        return
      }
      if (error) {
        setErrors({ submit: error.message })
        return
      }

      // Navigate back to profile - ensure we have a username
      const updatedUsername = data.username || formData.username
      if (updatedUsername) {
        navigate(`/profile/${updatedUsername}`)
      } else {
        // Fallback to profile setup if no username
        navigate('/profile-setup')
      }

    } catch (error) {
      console.error("Error updating profile:", error)
      setErrors({ submit: error.message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-dark">
      <PageMeta title="Edit Profile | Lilycove" description="Update your Lilycove profile details." />
      <div className="max-w-2xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-10 h-10 text-cream hover:text-purple hover:bg-dark/50 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-cream">Edit Profile</h1>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg p-4 mb-6">
            {errors.submit}
          </div>
        )}

        {/* Form */}
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-dark/50 rounded-lg p-6 border border-purple/20">
            <h2 className="text-lg font-semibold text-cream mb-4">Basic Information</h2>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-cream text-sm block">
                  Your Name
                </label>
                <input
                  type="text"
                  name="display_name"
                  value={formData.display_name}
                  onChange={handleInputChange}
                  placeholder="Your display name"
                  className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
                />
                {errors.display_name && <p className="text-purple text-xs mt-1">{errors.display_name}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-cream text-sm block">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Your username"
                  className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
                />
                {errors.username && <p className="text-purple text-xs mt-1">{errors.username}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-cream text-sm block">
                  Bio <span className="text-cream/50">(optional)</span>
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Tell us about yourself..."
                  rows={3}
                  className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50 resize-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-cream text-sm block">
                  Avatar URL <span className="text-cream/50">(optional)</span>
                </label>
                <input
                  type="url"
                  name="avatar_url"
                  value={formData.avatar_url}
                  onChange={handleInputChange}
                  placeholder="https://example.com/avatar.jpg"
                  className="w-full bg-dark border border-purple/30 text-cream rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple focus:border-purple placeholder:text-cream/50"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-dark/50 rounded-lg p-6 border border-purple/20">
            <LocationPicker
              value={locationData}
              onChange={handleLocationChange}
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple hover:bg-purple/90 text-cream font-medium px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-purple/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={18} />
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditProfilePage
